import { useEffect, useRef, useState } from 'react';
import { ScanLine, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { shipmentStatusService } from '@/services/shipmentStatusService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Found = { id: number; trackingNumber: string; statusName?: string; receiverName?: string; receiverCity?: string; codAmount: number; isApproved: boolean };

export default function ScanShipmentsPage() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [items, setItems] = useState<Found[]>([]);
  const [missing, setMissing] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [subStatuses, setSubStatuses] = useState<any[]>([]);
  const [statusId, setStatusId] = useState('');
  const [subStatusId, setSubStatusId] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await shipmentStatusService.getAll();
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setStatuses(list.filter((s: any) => s.isActive !== false));
      } catch { setMessage('تعذر تحميل الحالات.'); }
    })();
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    (async () => {
      setSubStatusId('');
      if (!statusId) { setSubStatuses([]); return; }
      try {
        const res = await api.get(`/api/shipment-sub-statuses?statusId=${statusId}`);
        setSubStatuses(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch { setSubStatuses([]); }
    })();
  }, [statusId]);

  // الماسح الضوئي يكتب الرقم ثم Enter؛ ندعم أيضاً لصق عدة أرقام مفصولة بسطر/فاصلة/مسافة
  const addCodes = async () => {
    const codes = code.split(/[\s,;]+/).map(c => c.trim()).filter(Boolean);
    setCode('');
    if (codes.length === 0) return;
    const fresh = codes.filter(c => !items.some(i => i.trackingNumber === c));
    if (fresh.length === 0) { setMessage('الرقم مضاف بالفعل.'); inputRef.current?.focus(); return; }
    try {
      const res = await api.post('/api/shipment-scan/resolve', { codes: fresh });
      const found: Found[] = res.data?.data?.found || [];
      const miss: string[] = res.data?.data?.missing || [];
      setItems(prev => [...found.filter(f => !prev.some(p => p.id === f.id)), ...prev]);
      setMissing(prev => Array.from(new Set([...miss, ...prev])));
      setMessage('');
    } catch { setMessage('تعذر التحقق من الأرقام.'); }
    inputRef.current?.focus();
  };

  const apply = async () => {
    if (!statusId || items.length === 0) return;
    const name = statuses.find(s => String(s.id) === statusId);
    if (!window.confirm(`تغيير حالة ${items.length} شحنة إلى "${name?.nameAr || name?.name}"؟ سيُرسل إشعار واتساب للمستلمين حسب إعدادات النظام.`)) return;
    setBusy(true); setMessage('');
    try {
      await api.put('/api/Shipments/bulk-status', {
        shipmentIds: items.map(i => i.id), statusId: Number(statusId), subStatusId: subStatusId ? Number(subStatusId) : null,
      });
      setMessage(`تم تحديث ${items.length} شحنة.`);
      setItems([]); setMissing([]);
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'حدث خطأ أثناء التحديث.');
    } finally { setBusy(false); inputRef.current?.focus(); }
  };

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <h1 className="text-2xl font-bold flex items-center gap-2"><ScanLine className="w-6 h-6" />مسح الشحنات وتحديث الحالة</h1>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-2">
            <Input ref={inputRef} value={code} onChange={e => setCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addCodes(); } }}
              placeholder="امسح الباركود أو اكتب رقم التتبع ثم Enter" autoFocus />
            <Button variant="outline" onClick={() => void addCodes()}>إضافة</Button>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <select className="border rounded-md h-10 px-2" value={statusId} onChange={e => setStatusId(e.target.value)}>
              <option value="">اختر الحالة الجديدة</option>
              {statuses.map(s => <option key={s.id} value={s.id}>{s.nameAr || s.name}</option>)}
            </select>
            <select className="border rounded-md h-10 px-2" value={subStatusId} onChange={e => setSubStatusId(e.target.value)} disabled={subStatuses.length === 0}>
              <option value="">الحالة الفرعية (اختياري)</option>
              {subStatuses.map((s: any) => <option key={s.id} value={s.id}>{s.nameAr || s.name}</option>)}
            </select>
            <Button disabled={busy || !statusId || items.length === 0} onClick={() => void apply()}>
              {busy ? 'جارٍ التحديث...' : `تطبيق على ${items.length} شحنة`}
            </Button>
          </div>
          {message && <p className="text-sm text-blue-700">{message}</p>}
          {missing.length > 0 && (
            <p className="text-sm text-red-600">غير موجودة: {missing.join('، ')}
              <button className="underline mr-2" onClick={() => setMissing([])}>إخفاء</button></p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>الشحنات الممسوحة ({items.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead><tr className="bg-gray-100 text-right">
              <th className="p-2 border">رقم التتبع</th><th className="p-2 border">المستلم</th><th className="p-2 border">المدينة</th>
              <th className="p-2 border">الحالة الحالية</th><th className="p-2 border">COD</th><th className="p-2 border"></th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td className="p-4 border text-center text-gray-500" colSpan={6}>لم تُمسح أي شحنة بعد</td></tr>}
              {items.map(i => (
                <tr key={i.id}>
                  <td className="p-2 border font-mono">{i.trackingNumber}{!i.isApproved && <span className="text-amber-600 mr-2 text-xs">غير معتمدة</span>}</td>
                  <td className="p-2 border">{i.receiverName}</td>
                  <td className="p-2 border">{i.receiverCity}</td>
                  <td className="p-2 border">{i.statusName}</td>
                  <td className="p-2 border">{i.codAmount}</td>
                  <td className="p-2 border"><button onClick={() => setItems(prev => prev.filter(p => p.id !== i.id))} aria-label="حذف"><Trash2 className="w-4 h-4 text-red-600" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
